import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../i18n/LanguageContext';
import { WHATSAPP } from '../../data/contact';
import { HERO_POSTER, HERO_VIDEO } from '../../data/media';
import Reveal from '../layout/Reveal';

// The only "special" section on the site: a muted background video (aurora until the clip exists).
// The video is skipped on data-saver and reduced-motion, and starts only after the page has loaded.
export default function HeroVideo() {
  const { t } = useLang();
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!HERO_VIDEO) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = navigator.connection && navigator.connection.saveData;
    if (reduce || saveData) return undefined;
    const start = () => { if (ref.current) { ref.current.src = HERO_VIDEO; ref.current.play().catch(() => {}); } };
    if (document.readyState === 'complete') { start(); return undefined; }
    window.addEventListener('load', start, { once: true });
    return () => window.removeEventListener('load', start);
  }, []);

  return (
    <section className="relative min-h-[92vh] flex items-end sm:items-center overflow-hidden bg-night text-white">
      <div className="aurora absolute inset-0" aria-hidden="true"><div className="blob b1" /><div className="blob b2" /><div className="blob b3" /><div className="blob b4" /></div>
      {HERO_VIDEO && (
        <video ref={ref} poster={HERO_POSTER || undefined} muted loop playsInline preload="none" aria-hidden="true"
          onPlaying={() => setPlaying(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${playing ? 'opacity-100' : 'opacity-0'}`} />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-night/85 via-night/50 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-night/70 to-transparent" />

      {!HERO_VIDEO && (
        <div className="hidden sm:block absolute right-8 bottom-10 text-xs bg-black/40 border border-white/25 rounded-xl px-3 py-2 max-w-[15rem] backdrop-blur">
          <div className="font-bold">▶ {t('vidTitle')}</div><div className="text-white/75 mt-0.5">{t('vidDesc')}</div>
        </div>
      )}

      <div className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 pt-28 pb-24 sm:pb-16">
        <Reveal className="max-w-2xl">
          <div className="flex flex-wrap gap-2 mb-6 text-xs font-semibold">
            <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/25 rounded-full px-3 py-1.5 backdrop-blur">🏅 {t('chipYears')}</span>
            <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/25 rounded-full px-3 py-1.5 backdrop-blur">★ {t('chipRating')}</span>
          </div>
          <h1 className="text-4xl sm:text-6xl font-extrabold leading-[1.05] tracking-tight">
            {t('h1a')}<br /><span className="text-amber-200">{t('h1b')}</span>
          </h1>
          <p className="mt-5 text-lg text-white/85 max-w-xl">{t('heroP')}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-ink font-bold px-6 py-3.5 rounded-full hover:-translate-y-0.5 transition shadow-xl">{t('ctaCall2')} →</Link>
            <a href={WHATSAPP} className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 font-bold px-6 py-3.5 rounded-full transition">💬 {t('ctaWa')}</a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
